import { API_ROOT } from '../components/api';
import authService from './authService';
import { buildBoundaryReference, buildLiveMapData } from '../components/stockout-map/liveMapData';

const PAGE_SIZE = 5000; // Existing db-query response limit; do not truncate countries.
const STOCK_DATABASE = process.env.REACT_APP_STOCKOUT_MAP_DATABASE || 'thrive_appdb_prd';
const FROM_DATE = process.env.REACT_APP_STOCKOUT_MAP_FROM_DATE || '2025-01-01';

function table(name) {
  if (!/^[A-Za-z0-9_]+$/.test(STOCK_DATABASE)) throw new Error('Invalid stockout map database setting.');
  return `\`${STOCK_DATABASE}\`.\`${name}\``;
}

async function queryRows(query, signal) {
  const token = await authService.getIdToken();
  const response = await fetch(`${API_ROOT}/db-query`, {
    method: 'POST', signal,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ query })
  });
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || 'Could not load the stockout map.');
  if (!Array.isArray(body.result)) throw new Error('Unexpected stockout map database response.');
  return body.result;
}

export function fetchMapCountries(signal) {
  return queryRows(`SELECT DISTINCT b.iso3, COALESCE(NULLIF(c.Country_Name, ''), b.iso3) AS country_name
    FROM ${table('geo_boundaries')} b LEFT JOIN ${table('geo_admin')} c ON c.iso3 = b.iso3
    WHERE b.geo_tier = 1 AND b.is_current = TRUE
      AND EXISTS (SELECT 1 FROM ${table('geo_boundaries')} a
        WHERE a.iso3 = b.iso3 AND a.is_current = TRUE AND a.geo_tier > 1 AND a.geometry IS NOT NULL)
    ORDER BY country_name`, signal);
}

export async function fetchMapData(iso3, signal) {
  if (!/^[A-Z]{3}$/.test(iso3)) throw new Error('Invalid country code.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(FROM_DATE)) throw new Error('Invalid stockout map start date setting.');
  const boundaries = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const page = await queryRows(`SELECT area_id, iso3, geo_tier, area_name, level_name, parent_area_id, geometry, attribution
      FROM ${table('geo_boundaries')} WHERE iso3 = '${iso3}' AND is_current = TRUE
      ORDER BY area_id LIMIT ${PAGE_SIZE} OFFSET ${offset}`, signal);
    boundaries.push(...page);
    if (page.length < PAGE_SIZE) break;
  }
  if (!boundaries.length) throw new Error('No geographic boundaries available for this country.');
  const reference = buildBoundaryReference(boundaries);
  const rows = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    // Read live zero-dose records; aggregate without saving any monthly data.
    const page = await queryRows(`SELECT DATE_FORMAT(v.Report_Date, '%Y-%m') AS report_month,
      v.SC_Level_No AS sc_level_no,
      CONVERT(v.Store_Name USING utf8mb4) COLLATE utf8mb4_bin AS store_name,
      CONVERT(v.Antigen_Name USING utf8mb4) COLLATE utf8mb4_bin AS antigen_name,
      b.area_id AS geo_area_id,
      COUNT(*) AS record_count
      FROM ${table('vw_ri_stocks')} v
      LEFT JOIN ${table('stores')} s ON s.iso3=v.iso3 AND s.SC_Level_No=v.SC_Level_No
        AND s.Store_Name=v.Store_Name AND BINARY s.Store_Name=BINARY v.Store_Name
      LEFT JOIN ${table('geo_boundaries')} b ON b.area_id=s.geo_area_id AND b.iso3=v.iso3 AND b.is_current=TRUE
      WHERE v.iso3 = '${iso3}' AND v.Doses = 0 AND v.Report_Date >= '${FROM_DATE}'
      GROUP BY DATE_FORMAT(v.Report_Date, '%Y-%m'), v.SC_Level_No,
        CONVERT(v.Store_Name USING utf8mb4) COLLATE utf8mb4_bin,
        CONVERT(v.Antigen_Name USING utf8mb4) COLLATE utf8mb4_bin, b.area_id
      ORDER BY report_month, sc_level_no, store_name, antigen_name, geo_area_id
      LIMIT ${PAGE_SIZE} OFFSET ${offset}`, signal);
    rows.push(...page);
    if (page.length < PAGE_SIZE) break;
  }
  return { geo: reference.geo, data: buildLiveMapData(rows, reference) };
}
